import useQuickAction from "../../hooks/useQuickAction";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { PencilAltIcon, PlusIcon } from "@heroicons/react/solid";
import AddDepartment from "./AddDepartment";
import { Button } from "../../components/ui";
import useGet from "../../hooks/read/useGet";
import useRead from "../../hooks/useRead";
import Spinner from "../../components/loader/Spinner";
import EditDepartment from "./EditDepartment";
import RoundedContainer from "../../components/containers/RoundedContainer";
import SearchBar from "../../components/inputs/SearchBar";
import Pagination from "../../components/pagination";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";

const LIMIT = 10;

function Department() {
  const [openDepartmentModal, setOpenDepartmentModal] = useState(false);
  const [openDepartmentAddModal, setOpenDepartmentAddModal] = useState(false);
  useQuickAction("add-department", () => setOpenDepartmentAddModal(true));
  const [selectedDepartment, setSelectedDepartment] = useState({});
  const { list: shiftList } = useRead({
    url: "shift_listing",
    method: "GET",
    initialData: {
      limit: 50,
      search_query: "",
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const pageNo = Number(searchParams.get("page_number")) || 0;
  const searchQuery = searchParams.get("search_query") || "";
  const [search, setSearch] = useState(searchQuery);
  const [prevUrlSearch, setPrevUrlSearch] = useState(searchQuery);

  // Keep the search box in sync with the URL (back/forward).
  if (searchQuery !== prevUrlSearch) {
    setSearch(searchQuery);
    setPrevUrlSearch(searchQuery);
  }

  const { list: departmentListing } = useGet({
    url: "department_listing",
    params: { limit: LIMIT, search_query: searchQuery.trim(), page_number: pageNo },
  });
  const departments = departmentListing.data?.data || [];

  // Empty values are dropped from the URL.
  const updateParams = (changes, options) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key)
    );
    setSearchParams(next, options);
  };

  // Searching goes back to the first page.
  const debouncedSearch = useDebouncedCallback((value) => {
    updateParams({ search_query: value, page_number: 0 }, { replace: true });
  }, 300);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handlePageChange = ({ selected }) => {
    updateParams({ page_number: selected });
  };

  const handleOpenEditModal = (id) => {
    setSelectedDepartment(departments.find((x) => x.id === id));
    setOpenDepartmentModal(true);
  };

  return (
    <div className="py-10">
      {shiftList.isLoading && <Spinner />}
      <div className="px-4 flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <SearchBar
            placeholder="Search by department name..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        <Button onClick={() => setOpenDepartmentAddModal(true)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Department
        </Button>
      </div>

      {departmentListing.isLoading ? (
        <div className="flex justify-center items-center min-h-96">
          <PrimaryLoader />
        </div>
      ) : departmentListing.isError ? (
        <RoundedContainer className="mx-4 text-center py-12">
          <p className="text-gray-700 text-2xl font-semibold mb-2">Couldn't Load Departments</p>
          <p className="text-gray-500 text-sm mb-5">
            Something went wrong while fetching your departments.
          </p>
          <Button variant="outline" onClick={() => departmentListing.refetch()}>
            Try Again
          </Button>
        </RoundedContainer>
      ) : (
        <>
          <RoundedContainer className="mx-4">
            {!departments.length && searchQuery ? (
              <div className="text-center py-12">
                <p className="text-gray-700 text-2xl font-semibold mb-2">No Departments Found</p>
                <p className="text-gray-500 text-sm">
                  No departments match "{searchQuery}". Try a different search.
                </p>
              </div>
            ) : !departments.length ? (
              <div className="text-center py-12">
                <p className="text-gray-700 text-2xl font-semibold mb-2">No Departments To Show</p>
                <p className="text-gray-500 text-sm mb-5">Create departments for your organization to organize your workforce.</p>
                <Button onClick={() => setOpenDepartmentAddModal(true)}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Department
                </Button>
              </div>
            ) : (
              <div className="max-w-full overflow-x-auto">
                <Table className="table-auto w-full">
                  <TableHead>
                    <TableHeaderCell className="w-[10%] text-center">
                      Sr.no
                    </TableHeaderCell>
                    <TableHeaderCell className="w-[70%]">
                      Department Name
                    </TableHeaderCell>
                    <TableHeaderCell className="w-[20%] text-center">
                      Modify
                    </TableHeaderCell>
                  </TableHead>
                  <TableBody>
                    {departments.map(({ id, departmentName }, idx) => (
                      <TableRow key={id}>
                        <TableCell className="text-dark font-medium text-sm text-center">
                          {pageNo * LIMIT + idx + 1}
                        </TableCell>
                        <TableCell className="text-dark font-medium text-sm">
                          {departmentName}
                        </TableCell>
                        <TableCell className="text-center">
                          <div
                            className="w-6 h-6 cursor-pointer mx-auto"
                            onClick={() => handleOpenEditModal(id)}
                          >
                            <PencilAltIcon className="text-gray-500 w-full h-full hover:text-primary transition-colors" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </RoundedContainer>

          {!!departmentListing.data?.total_count && (
            <RoundedContainer className="mx-4 mt-4">
              <Pagination
                currentPage={pageNo}
                lengthofItems={departmentListing.data.total_count}
                limit={LIMIT}
                onPageChange={handlePageChange}
              />
            </RoundedContainer>
          )}
        </>
      )}

      {openDepartmentModal && (
        <EditDepartment
          open={openDepartmentModal}
          shiftData={shiftList?.data?.data}
          onClose={() => setOpenDepartmentModal(false)}
          selectedDepartment={selectedDepartment}
        />
      )}
      {openDepartmentAddModal && (
        <AddDepartment
          shiftData={shiftList?.data?.data}
          open={openDepartmentAddModal}
          onClose={() => setOpenDepartmentAddModal(false)}
        />
      )}
    </div>
  );
}

export default Department;
